import { __assign, __awaiter, __generator } from "tslib";
import React, { useCallback, useMemo, useState } from 'react';
import { Grid, FormControl, FormLabel, Input, Button, FormErrorMessage, useTheme, Collapse } from '@chakra-ui/react';
import { useStripe, useElements, CardNumberElement, CardCvcElement, CardExpiryElement } from '@stripe/react-stripe-js';
import { useFormik, FormikProvider, Form, Field } from 'formik';
import { CheckBoxLabel } from "../CheckBoxLabel";
export var AddStripeCreditCardForm = function AddStripeCreditCardForm(props) {
  var _onSubmit = props.onSubmit;
  var stripe = useStripe();
  var elements = useElements();
  var theme = useTheme();

  var _a = useState(false),
      isLoading = _a[0],
      setLoading = _a[1];

  var formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
      isPrimary: true
    },
    validate: function validate(values) {
      var errors = {};

      if (!values.name) {
        errors.name = 'จำเป็นต้องใส่ชื่อผู้ถือบัตร';
      }

      if (!values.cardNumber) {
        errors.cardNumber = getErrors({
          code: 'incomplete_number'
        });
      }

      if (!values.cardExpiry) {
        errors.cardExpiry = getErrors({
          code: 'incomplete_expiry'
        });
      }

      if (!values.cardCvc) {
        errors.cardCvc = getErrors({
          code: 'incomplete_cvc'
        });
      }

      return errors;
    },
    onSubmit: function onSubmit(values) {
      return __awaiter(void 0, void 0, void 0, function () {
        var cardNumberElement, cardExpiryElement, cardCvcElement, err_1;
        return __generator(this, function (_a) {
          switch (_a.label) {
            case 0:
              if (!values.name || !values.cardExpiry || !values.cardExpiry || !values.cardCvc) return [2
              /*return*/
              ];

              if (!elements || !stripe) {
                throw new Error('Stripe provider not found, please make sure you are following the https://stripe.com/docs/stripe-js/react document');
                return [2
                /*return*/
                ];
              }

              cardNumberElement = elements.getElement(CardNumberElement);
              cardExpiryElement = elements.getElement(CardExpiryElement);
              cardCvcElement = elements.getElement(CardCvcElement);
              if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) return [2
              /*return*/
              ];
              setLoading(true);
              _a.label = 1;

            case 1:
              _a.trys.push([1, 3,, 4]);

              return [4
              /*yield*/
              , _onSubmit({
                type: 'card',
                card: cardNumberElement,
                billing_details: {
                  name: values.name
                }
              }, values.isPrimary)];

            case 2:
              _a.sent();

              setLoading(false);
              return [3
              /*break*/
              , 4];

            case 3:
              err_1 = _a.sent();
              setLoading(false);
              return [3
              /*break*/
              , 4];

            case 4:
              return [2
              /*return*/
              ];
          }
        });
      });
    }
  });
  var getErrors = useCallback(function (error) {
    if (!error) return '';

    switch (error.code) {
      case 'empty_expiry':
        return 'จำเป็นต้องใส่วันหมดอายุบัตร';

      case 'empty_cvc':
        return 'จำเป็นต้องใส่รหัส';

      case 'empty_number':
        return 'จำเป็นต้องใส่เลขบัตรเครดิต';

      case 'invalid_number':
      case 'incomplete_number':
        return 'เลขบัตรไม่ถูกต้อง';

      case 'invalid_expiry_month_past':
      case 'invalid_expiry_year_past':
        return 'บัตรหมดอายุ';

      case 'invalid_expiry_year':
      case 'incomplete_expiry':
        return 'วันหมดอายุไม่ถูกต้อง';

      case 'incomplete_cvc':
        return 'รหัส CVC ไม่ถูกต้อง';

      default:
        return '';
    }
  }, []);
  var handleCardNumberChange = useCallback(function (event) {
    if (formik.touched.cardNumber && event.empty) {
      formik.setFieldError('cardNumber', getErrors({
        code: 'empty_number'
      }));
      return;
    }

    if (!event.empty && !formik.touched.cardNumber) {
      formik.setFieldTouched('cardNumber', true);
    }

    if (event.complete) {
      formik.setFieldValue('cardNumber', true);
    } else {
      formik.setFieldValue('cardNumber', false);
    }

    formik.setFieldError('cardNumber', getErrors(event.error));
  }, [formik, getErrors]);
  var handleCardExpiryChange = useCallback(function (event) {
    if (formik.touched.cardExpiry && event.empty) {
      formik.setFieldError('cardExpiry', getErrors({
        code: 'empty_expiry'
      }));
      return;
    }

    if (!event.empty && !formik.touched.cardExpiry) {
      formik.setFieldTouched('cardExpiry', true);
    }

    if (event.complete) {
      formik.setFieldValue('cardExpiry', true);
    } else {
      formik.setFieldValue('cardExpiry', false);
    }

    formik.setFieldError('cardExpiry', getErrors(event.error));
  }, [formik, getErrors]);
  var handleCardCvcChange = useCallback(function (event) {
    if (formik.touched.cardCvc && event.empty) {
      formik.setFieldError('cardCvc', getErrors({
        code: 'empty_cvc'
      }));
      return;
    }

    if (!event.empty && !formik.touched.cardCvc) {
      formik.setFieldTouched('cardCvc', true);
    }

    if (event.complete) {
      formik.setFieldValue('cardCvc', true);
    } else {
      formik.setFieldValue('cardCvc', false);
    }

    formik.setFieldError('cardCvc', getErrors(event.error));
  }, [formik, getErrors]);
  var handleClearForm = useCallback(function () {
    if (!elements) return;
    var cardNumberElement = elements.getElement(CardNumberElement);
    var cardExpiryElement = elements.getElement(CardExpiryElement);
    var cardCvcElement = elements.getElement(CardCvcElement);
    if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) return;
    formik.resetForm();
    cardNumberElement.clear();
    cardExpiryElement.clear();
    cardCvcElement.clear();
  }, [formik, elements]);
  var inputStyle = useMemo(function () {
    var _a, _b;

    return {
      base: {
        fontSize: theme.components.Input.baseStyle.field.fontSize || '16px',
        fontWeight: theme.components.Input.baseStyle.field.fontWeight || '400',
        fontFamily: theme.fonts.body,
        color: theme.components.Input.baseStyle.field.color,
        fontSmoothing: 'antialiased',
        '::placeholder': {
          color: ((_a = theme.components.Input.baseStyle.field._placeholder) === null || _a === void 0 ? void 0 : _a.color) || '#A0AEC0',
          fontWeight: ((_b = theme.components.Input.baseStyle.field._placeholder) === null || _b === void 0 ? void 0 : _b.fontWeight) || '400'
        }
      }
    };
  }, []);
  if (!elements || !stripe) return null;
  return /*#__PURE__*/React.createElement(FormikProvider, {
    value: formik
  }, /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Grid, {
    gap: "4"
  }, /*#__PURE__*/React.createElement(Field, {
    name: "name"
  }, function (_a) {
    var field = _a.field,
        form = _a.form;
    var isInvalid = Boolean(form.errors.name) && Boolean(form.touched.name);
    return /*#__PURE__*/React.createElement(FormControl, {
      isInvalid: isInvalid
    }, /*#__PURE__*/React.createElement(FormLabel, {
      htmlFor: "name"
    }, "\u0E0A\u0E37\u0E48\u0E2D\u0E1C\u0E39\u0E49\u0E16\u0E37\u0E2D\u0E1A\u0E31\u0E15\u0E23"), /*#__PURE__*/React.createElement(Input, __assign({
      id: "name",
      placeholder: "\u0E0A\u0E37\u0E48\u0E2D\u0E1C\u0E39\u0E49\u0E16\u0E37\u0E2D\u0E1A\u0E31\u0E15\u0E23"
    }, field)), /*#__PURE__*/React.createElement(Collapse, {
      in: isInvalid,
      animateOpacity: true
    }, /*#__PURE__*/React.createElement(FormErrorMessage, null, form.errors.name)));
  }), /*#__PURE__*/React.createElement(Field, {
    name: "cardNumber"
  }, function (_a) {
    var form = _a.form;
    var isInvalid = Boolean(form.errors.cardNumber) && Boolean(form.touched.cardNumber);
    return /*#__PURE__*/React.createElement(FormControl, {
      isInvalid: isInvalid
    }, /*#__PURE__*/React.createElement(FormLabel, {
      htmlFor: "cardNumber"
    }, "\u0E2B\u0E21\u0E32\u0E22\u0E40\u0E25\u0E02\u0E1A\u0E31\u0E15\u0E23\u0E40\u0E04\u0E23\u0E14\u0E34\u0E15"), /*#__PURE__*/React.createElement(CardNumberElement, {
      id: "cardNumber",
      options: {
        showIcon: true,
        placeholder: 'หมายเลขบัตรเครดิต',
        style: inputStyle,
        classes: {
          base: isInvalid ? 'StripeElement StripeElement--invalid' : 'StripeElement'
        }
      },
      onChange: handleCardNumberChange,
      onBlur: function onBlur() {
        return formik.setFieldTouched('cardNumber');
      }
    }), /*#__PURE__*/React.createElement(Collapse, {
      in: isInvalid,
      animateOpacity: true
    }, /*#__PURE__*/React.createElement(FormErrorMessage, null, form.errors.cardNumber)));
  }), /*#__PURE__*/React.createElement(Grid, {
    gridTemplateColumns: "1fr 136px",
    gap: "4"
  }, /*#__PURE__*/React.createElement(Field, {
    name: "cardExpiry"
  }, function (_a) {
    var form = _a.form;
    var isInvalid = Boolean(form.errors.cardExpiry) && Boolean(form.touched.cardExpiry);
    return /*#__PURE__*/React.createElement(FormControl, {
      isInvalid: isInvalid
    }, /*#__PURE__*/React.createElement(FormLabel, {
      htmlFor: "cardExpiry"
    }, "\u0E27\u0E31\u0E19\u0E2B\u0E21\u0E14\u0E2D\u0E32\u0E22\u0E38\u0E1A\u0E31\u0E15\u0E23 MM / YY"), /*#__PURE__*/React.createElement(CardExpiryElement, {
      options: {
        placeholder: 'วันหมดอายุบัตร MM / YY',
        style: inputStyle,
        classes: {
          base: isInvalid ? 'StripeElement StripeElement--invalid' : 'StripeElement'
        }
      },
      onChange: handleCardExpiryChange,
      onBlur: function onBlur() {
        return formik.setFieldTouched('cardExpiry');
      }
    }), /*#__PURE__*/React.createElement(Collapse, {
      in: isInvalid,
      animateOpacity: true
    }, /*#__PURE__*/React.createElement(FormErrorMessage, null, form.errors.cardExpiry)));
  }), /*#__PURE__*/React.createElement(Field, {
    name: "cardCvc"
  }, function (_a) {
    var form = _a.form;
    var isInvalid = Boolean(form.errors.cardCvc) && Boolean(form.touched.cardCvc);
    return /*#__PURE__*/React.createElement(FormControl, {
      isInvalid: isInvalid
    }, /*#__PURE__*/React.createElement(FormLabel, {
      htmlFor: "cardCvc"
    }, "\u0E23\u0E2B\u0E31\u0E2A CVC"), /*#__PURE__*/React.createElement(CardCvcElement, {
      options: {
        placeholder: 'รหัส CVC',
        style: inputStyle,
        classes: {
          base: isInvalid ? 'StripeElement StripeElement--invalid' : 'StripeElement'
        }
      },
      onChange: handleCardCvcChange,
      onBlur: function onBlur() {
        return formik.setFieldTouched('cardCvc');
      }
    }), /*#__PURE__*/React.createElement(Collapse, {
      in: isInvalid,
      animateOpacity: true
    }, /*#__PURE__*/React.createElement(FormErrorMessage, null, form.errors.cardCvc)));
  })), /*#__PURE__*/React.createElement(Field, {
    name: "isPrimary"
  }, function (_a) {
    var field = _a.field,
        form = _a.form;
    return /*#__PURE__*/React.createElement(CheckBoxLabel, {
      isChecked: field.value,
      onChange: function onChange(checked) {
        return form.setFieldValue('isPrimary', checked);
      }
    }, "\u0E1A\u0E31\u0E19\u0E17\u0E36\u0E01\u0E40\u0E1B\u0E47\u0E19\u0E1A\u0E31\u0E15\u0E23\u0E40\u0E23\u0E34\u0E48\u0E21\u0E15\u0E49\u0E19");
  }), /*#__PURE__*/React.createElement(Button, {
    width: "full",
    type: "submit",
    size: "lg",
    variant: "solid",
    colorScheme: "brand",
    mt: "2",
    isLoading: isLoading
  }, "\u0E1A\u0E31\u0E19\u0E17\u0E36\u0E01\u0E1A\u0E31\u0E15\u0E23\u0E43\u0E1A\u0E19\u0E35\u0E49"))));
};